/**
 * Shell support button (gap row 48, I8): header icon with the unread-reply
 * badge; opens a right-side drawer hosting <SupportPanel/>. Polls the unread
 * count every minute (and on window focus) so a staff reply surfaces without a
 * reload. Other parts of the app can open it via `openSupport(threadId?)`.
 */

import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { useT } from '@/i18n';
import { OPEN_SUPPORT_EVENT, supportApi } from '@/lib/api/support';

import { SupportPanel } from './SupportPanel';

const POLL_MS = 60_000;

export function SupportButton() {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [threadId, setThreadId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await supportApi.unread();
      setUnread(res.unread);
    } catch {
      // Support must never break the shell: a failed poll keeps the last count.
    }
  }, []);

  useEffect(() => {
    void refresh();
    const id = window.setInterval(() => void refresh(), POLL_MS);
    const onFocus = () => void refresh();
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<{ threadId?: string }>).detail;
      setThreadId(detail?.threadId ?? null);
      setOpen(true);
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener(OPEN_SUPPORT_EVENT, onOpen);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(OPEN_SUPPORT_EVENT, onOpen);
    };
  }, [refresh]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setThreadId(null);
          setOpen(true);
        }}
        aria-label={t('support.buttonAria', { count: unread })}
        title={t('support.button')}
        data-testid="support-button"
        className="relative inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-token p-2 text-muted transition-colors hover:bg-surface-2 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 13a8 8 0 0 1 16 0" />
          <path d="M4 13v3a2 2 0 0 0 2 2h1v-6H6a2 2 0 0 0-2 2zM20 13v3a2 2 0 0 1-2 2h-1v-6h1a2 2 0 0 1 2 2z" />
          <path d="M17 18c0 1.7-2.2 3-5 3" />
        </svg>
        {unread > 0 && (
          <span
            data-testid="support-unread-badge"
            className="absolute right-1 top-1 min-w-[18px] rounded-full bg-danger px-1 text-center text-[10px] font-bold leading-[18px] text-primary-foreground"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {/* Portalled to <body>: the shell header is `um-glass` (backdrop-filter), which
          makes it the containing block of `position: fixed` descendants — rendered in
          place, the drawer was clipped to the 56 px header and the panel had zero
          height (gap-verify, prod 2026-10-29). */}
      {open && createPortal(
        <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label={t('support.panelTitle')}>
          <button type="button" aria-label="×" className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <aside className="relative flex h-full w-full max-w-md flex-col gap-3 border-s border-border bg-surface p-4 shadow-elevation-3">
            <header className="flex items-start gap-2">
              <div>
                <h2 className="text-base font-semibold">{t('support.panelTitle')}</h2>
                <p className="text-xs text-muted">{t('support.panelSubtitle')}</p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="ms-auto rounded-token p-1 text-muted hover:bg-surface-2"
                aria-label={t('common.close')}
                data-testid="support-close"
              >
                ✕
              </button>
            </header>
            <div className="min-h-0 flex-1">
              <SupportPanel initialThreadId={threadId} onUnreadChange={setUnread} />
            </div>
          </aside>
        </div>,
        document.body,
      )}
    </>
  );
}
